import {
  ClaimReservationCommunicationMethod,
  ClaimReservationWithTimeslot,
  ResendClaimFormNotificationResponse,
} from '@Generated/back-office-client/src'
import { Button, Dialog, Flex, IconButton, Text } from '@radix-ui/themes'
import { ArrowLeftIcon, Cross2Icon } from '@radix-ui/react-icons'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ClaimReservationsServiceAPI } from '@API/data-services/claim-reservations-service'

type DeliveryMethod = ClaimReservationCommunicationMethod

export const ResendClaimFormPopup = ({
  reservation,
  onBack,
}: {
  reservation: ClaimReservationWithTimeslot
  onBack: () => void
}) => {
  const details = reservation.claimReservationDetails
  const hasPhone = !!details.phone
  const hasEmail = !!details.email

  const getInitialMethod = (): DeliveryMethod => {
    const preferred = details.communicationPreference as DeliveryMethod
    if (preferred === ClaimReservationCommunicationMethod.PHONE && hasPhone) {
      return ClaimReservationCommunicationMethod.PHONE
    }
    if (preferred === ClaimReservationCommunicationMethod.EMAIL && hasEmail) {
      return ClaimReservationCommunicationMethod.EMAIL
    }
    if (hasPhone) {
      return ClaimReservationCommunicationMethod.PHONE
    }
    if (hasEmail) {
      return ClaimReservationCommunicationMethod.EMAIL
    }
    return ClaimReservationCommunicationMethod.PHONE
  }

  const [selectedMethod, setSelectedMethod] = useState<DeliveryMethod>(getInitialMethod())
  const [errorMessage, setErrorMessage] = useState<string>()
  const [sentResponse, setSentResponse] = useState<ResendClaimFormNotificationResponse>()

  const { mutate: sendNotification, isPending } = useMutation({
    mutationFn: () => {
      setErrorMessage(undefined)
      return ClaimReservationsServiceAPI.resendClaimFormNotification(reservation.id, selectedMethod)
    },
    onSuccess: (response) => {
      setSentResponse(response)
    },
    onError: () => {
      setErrorMessage('Failed to send notification. Please try again.')
    },
  })

  if (sentResponse) {
    const viaText =
      sentResponse.method === ClaimReservationCommunicationMethod.PHONE ? 'text' : 'email'
    return (
      <Flex direction="column" align="center" gap="4" style={{ padding: '18px 48px 48px 48px' }}>
        <Flex justify="between" style={{ width: '100%' }}>
          <Text size="2" style={{ cursor: 'pointer' }} onClick={onBack}>
            <Flex align="center" gap="1">
              <ArrowLeftIcon /> Back
            </Flex>
          </Text>
          <Dialog.Close>
            <IconButton variant="ghost" aria-label="Close">
              <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
            </IconButton>
          </Dialog.Close>
        </Flex>

        <Text weight="bold" size="4">
          Claim Form Resent
        </Text>
        <Text size="2" color="gray" align="center">
          Sent via {viaText} to
          <br />
          <Text weight="bold">{sentResponse.sentTo}</Text>
        </Text>
        <Button
          style={{ backgroundColor: 'black', color: 'white', marginTop: '12px' }}
          onClick={onBack}>
          Close
        </Button>
      </Flex>
    )
  }

  return (
    <Flex direction="column" align="center" gap="4" style={{ padding: '18px 48px 48px 48px' }}>
      <Flex justify="between" style={{ width: '100%' }}>
        <Text size="2" style={{ cursor: 'pointer' }} onClick={onBack}>
          <Flex align="center" gap="1">
            <ArrowLeftIcon /> Back
          </Flex>
        </Text>
        <Dialog.Close>
          <IconButton variant="ghost" aria-label="Close">
            <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
          </IconButton>
        </Dialog.Close>
      </Flex>

      <Text weight="bold" size="4" align="center">
        Resend Claim Form?
      </Text>

      <Flex direction="column" gap="2" style={{ width: '100%', maxWidth: '280px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            border: `2px solid ${selectedMethod === ClaimReservationCommunicationMethod.PHONE ? '#4a90d9' : '#ddd'}`,
            borderRadius: '8px',
            background:
              selectedMethod === ClaimReservationCommunicationMethod.PHONE
                ? '#f0f6ff'
                : 'transparent',
            cursor: hasPhone ? 'pointer' : 'not-allowed',
            opacity: hasPhone ? 1 : 0.4,
          }}
          onClick={() => {
            if (hasPhone) {
              setSelectedMethod(ClaimReservationCommunicationMethod.PHONE)
            }
          }}>
          <input
            type="radio"
            name="delivery-method"
            checked={selectedMethod === ClaimReservationCommunicationMethod.PHONE}
            disabled={!hasPhone}
            onChange={() => {
              setSelectedMethod(ClaimReservationCommunicationMethod.PHONE)
            }}
          />
          <Flex direction="column">
            <Text weight="medium" size="2">
              Phone
            </Text>
            <Text size="1" color="gray">
              {details.phone ?? 'No phone on file'}
            </Text>
          </Flex>
        </label>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            border: `2px solid ${selectedMethod === ClaimReservationCommunicationMethod.EMAIL ? '#4a90d9' : '#ddd'}`,
            borderRadius: '8px',
            background:
              selectedMethod === ClaimReservationCommunicationMethod.EMAIL
                ? '#f0f6ff'
                : 'transparent',
            cursor: hasEmail ? 'pointer' : 'not-allowed',
            opacity: hasEmail ? 1 : 0.4,
          }}
          onClick={() => {
            if (hasEmail) {
              setSelectedMethod(ClaimReservationCommunicationMethod.EMAIL)
            }
          }}>
          <input
            type="radio"
            name="delivery-method"
            checked={selectedMethod === ClaimReservationCommunicationMethod.EMAIL}
            disabled={!hasEmail}
            onChange={() => {
              setSelectedMethod(ClaimReservationCommunicationMethod.EMAIL)
            }}
          />
          <Flex direction="column">
            <Text weight="medium" size="2">
              Email
            </Text>
            <Text size="1" color="gray">
              {details.email ?? 'No email on file'}
            </Text>
          </Flex>
        </label>
      </Flex>

      {errorMessage && (
        <Text weight="bold" color="red" size="2">
          {errorMessage}
        </Text>
      )}

      <Button
        style={{ backgroundColor: 'black', color: 'white', padding: '0 32px' }}
        loading={isPending}
        disabled={(!hasPhone && !hasEmail) || isPending}
        onClick={() => {
          sendNotification()
        }}>
        Resend
      </Button>
    </Flex>
  )
}
